// src/components/ExpensesTable.tsx
import React, { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Table, ConfigProvider, Tag } from 'antd';
import ruRU from 'antd/es/locale/ru_RU';
import { useResizableColumns, ResCol } from '../hooks/useResizableColumns';
import { formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';

type ExpenseLine = {
  name?: string;
  qty?: number;
  unitCost?: number;
  totalCost?: number;
  category?: string;
};
type Expense = {
  id?: string;
  createdAt: string;
  vendor?: string;       // иногда "поставщик" может приходить как vendor
  supplier?: string;     // или как supplier — учтём оба
  category?: string;
  description?: string;
  status?: string;
  total?: number;
  totalCost?: number;
  lines?: ExpenseLine[];
};

type Row = {
  key: string;
  expenseId: string;
  dateTs: number;
  category: string;
  vendor: string;
  description: string;
  status: string;
  totalCost: number | null;
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  paid:       { label: 'Оплачено',      color: 'green'  },
  planned:    { label: 'Запланировано', color: 'blue'   },
  processing: { label: 'В процессе',    color: 'orange' },
  canceled:   { label: 'Отменено',      color: 'red'    },
};

const ExpensesTable: React.FC<{ expenses: Expense[]; maxRows?: number }> = ({ expenses, maxRows = 500 }) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const e of expenses ?? []) {
      const expenseId = String(e?.id ?? '—');
      const dateTs = new Date(e.createdAt).getTime();
      const vendor = String(e.vendor ?? e.supplier ?? '—');
      const status = String(e.status ?? '—');

      // Сумма: явное поле total/totalCost или суммуем позиции
      let total: number | null = null;
      if (typeof e.total === 'number') total = e.total;
      else if (typeof e.totalCost === 'number') total = e.totalCost;
      else if (Array.isArray(e.lines) && e.lines.length) {
        total = e.lines.reduce((s, l) => {
          const qty = Number(l.qty ?? 0);
          const unit = l.unitCost != null ? Number(l.unitCost) : null;
          const lineTotal = l.totalCost != null ? Number(l.totalCost) : unit != null ? unit * qty : 0;
          return s + (Number.isFinite(lineTotal) ? lineTotal : 0);
        }, 0);
      }

      // Тип расхода (бывшее description): "Единичный расход" / "Позиционный расход"
      const expenseType = String(e.description ?? '—');

      // Реальное описание: что куплено — имена позиций или категория + поставщик
      const lineNames = Array.isArray(e.lines) && e.lines.length
        ? e.lines.map(l => l.name).filter(Boolean).join(', ')
        : '';
      const cat = String(e.category ?? e.lines?.[0]?.category ?? '—');
      const realDesc = lineNames
        ? lineNames
        : vendor !== '—'
          ? `${vendor}${cat !== '—' ? ' — ' + cat : ''}`
          : cat;

      out.push({
        key: expenseId,
        expenseId: expenseType,   // колонка "Расход" показывает тип
        dateTs,
        category: cat,
        vendor,
        description: realDesc,   // колонка "Описание" показывает что куплено
        status,
        totalCost: total,
      });
    }
    out.sort((a, b) => b.dateTs - a.dateTs);
    return out.slice(0, maxRows);
  }, [expenses, maxRows]);

  const baseColumns: ResCol<Row>[] = useMemo(
    () => [
      { key: 'expenseId', title: 'Расход', dataIndex: 'expenseId',
        width: 160, minWidth: 130, align: 'left', ellipsis: true,
        sorter: (a, b) => a.expenseId.localeCompare(b.expenseId, 'ru') },
      { key: 'dateTs', title: 'Дата', dataIndex: 'dateTs',
        width: 170, minWidth: 150, align: 'left',
        render: (ts: number) => formatDateTime(ts),
        sorter: (a, b) => a.dateTs - b.dateTs, defaultSortOrder: 'descend' as const },
      { key: 'category', title: 'Категория', dataIndex: 'category',
        width: 160, minWidth: 140, align: 'left', ellipsis: true,
        sorter: (a, b) => a.category.localeCompare(b.category, 'ru') },
      { key: 'vendor', title: 'Поставщик', dataIndex: 'vendor',
        width: 180, minWidth: 150, align: 'left', ellipsis: true,
        sorter: (a, b) => a.vendor.localeCompare(b.vendor, 'ru') },
      { key: 'description', title: 'Описание', dataIndex: 'description',
        width: 220, minWidth: 160, align: 'left', ellipsis: true,
        sorter: (a, b) => a.description.localeCompare(b.description, 'ru') },
      { key: 'status', title: 'Статус', dataIndex: 'status',
        width: 140, minWidth: 120, align: 'left',
        render: (v: string) => {
          const m = STATUS_LABELS[v] ?? { label: v, color: 'default' };
          return <Tag color={m.color}>{m.label}</Tag>;
        },
        sorter: (a, b) => a.status.localeCompare(b.status, 'ru') },
      { key: 'totalCost', title: 'Сумма', dataIndex: 'totalCost',
        width: 140, minWidth: 120, align: 'left',
        render: (v: number | null) => (v == null ? '—' : <b>{formatCurrency(v, 'AMD')}</b>),
        sorter: (a, b) => (a.totalCost ?? -Infinity) - (b.totalCost ?? -Infinity) },
    ],
    []
  );

  const { columns, components } = useResizableColumns<Row>(baseColumns, {
    storageKey: 'table.expenses.cols.v1',
    enableVisibility: true,
    enableReorder: true,
  });

  const locale = { ...ruRU, Pagination: { ...(ruRU as any).Pagination, items_per_page: '' } } as any;

  return (
    <ConfigProvider locale={locale}>
      <Table<Row>
        size="small"
        rowKey="key"
        dataSource={rows}
        columns={columns as ColumnsType<Row>}
        components={components}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        pagination={{
          current, pageSize, position: ['bottomRight'], showSizeChanger: true,
          pageSizeOptions: ['20','50','100'],
          onChange: (p, s) => { setCurrent(p); setPageSize(s); },
          showLessItems: true,
        }}
      />
    </ConfigProvider>
  );
};

export default ExpensesTable;
